import { useQuery } from '@tanstack/react-query';
import { fmtDag, fmtUren, ITEM_STATUS_LABEL, taakStatus, vandaag, type ItemStatus } from '@ep/core';
import { api } from '../lib/api';
import { useActie, useIk, useModule, type TaakRij } from '../lib/data';
import { Badge, Laden } from '../ui/ui';
import { useState } from 'react';
import { StartTimer, Stand, TijdToevoegen, useTimer, type Doel } from '../ui/Uren';

interface SprintItem { id: string; naam: string; uren: number; status: ItemStatus; eigenaarId: string | null; eigenaarNaam: string | null; werkzaamheden?: string | null; besteedMin?: number; restUren?: number | null; projectId?: string }
interface MijnSprint {
  sprint: { id: string; nummer: number; start: string; eind: string; doel: string | null };
  werkpakket: { id: string; naam: string };
  project: { id: string; nummer: string | null; naam: string };
  items: SprintItem[];
  backlog: SprintItem[];
}
const TOON: Record<ItemStatus, 'grijs' | 'blauw' | 'oranje' | 'accent' | 'groen'> = { BACKLOG: 'grijs', GEPLAND: 'blauw', BEZIG: 'oranje', REVIEW: 'accent', KLAAR: 'groen' };

/** SPEC §9.4: engineer pakt zelf items op in de lopende sprint van zijn team. */
function MijnSprintPaneel() {
  const ik = useIk().data;
  const agileAan = useModule('AGILE');
  const urenAan = useModule('UREN');
  const q = useQuery({ queryKey: ['mijn-sprint'], queryFn: () => api.get<{ sprints: MijnSprint[] }>('/api/mijn-sprint'), enabled: agileAan });
  const zet = useActie((a: { id: string; body: Record<string, unknown> }) => api.patch(`/api/items/${a.id}`, a.body), { opNa: () => q.refetch() });
  const timer = useTimer().data?.timer;
  const [start, setStart] = useState<Doel | null>(null);
  const [tijd, setTijd] = useState<Doel | null>(null);
  if (!agileAan || !q.data?.sprints.length || !ik?.eid) return null;
  const eid = ik.eid;
  const knop = (label: string, id: string, body: Record<string, unknown>, primair = false) => (
    <button className={`knop klein${primair ? ' primair' : ''}`} disabled={zet.isPending} onClick={() => zet.mutate({ id, body })}>{label}</button>
  );
  return (
    <>
      {q.data.sprints.map((m) => {
        const mijn = m.items.filter((i) => i.eigenaarId === eid && i.status !== 'KLAAR');
        const vrij = m.items.filter((i) => !i.eigenaarId && i.status !== 'KLAAR');
        const overig = m.items.filter((i) => (i.eigenaarId && i.eigenaarId !== eid) || (i.status === 'KLAAR' && i.eigenaarId === eid));
        return (
          <div className="paneel" key={m.sprint.id}>
            <h2 style={{ marginBottom: 4 }}>Mijn sprint · {m.project.nummer ?? ''} {m.werkpakket.naam}</h2>
            <p className="mute klein" style={{ marginBottom: 8 }}>
              Sprint {m.sprint.nummer}, {fmtDag(m.sprint.start)} – {fmtDag(m.sprint.eind)}{m.sprint.doel ? ` · Doel: ${m.sprint.doel}` : ''}
            </p>
            <div className="rij"><h3 className="groei">Mijn items</h3>{urenAan && <button className="knop klein" title="Overleg en ander werk voor dit werkpakket, zonder item" onClick={() => setTijd({ soort: 'WERKPAKKET', id: m.werkpakket.id, label: `${m.werkpakket.naam} (overleg en overig)`, sub: 'werkpakket', projectId: m.project.id, projectNummer: m.project.nummer, projectNaam: m.project.naam })}>+ Tijd op werkpakket</button>}</div>
            {mijn.length ? (
              <table className="tabel">
                <thead><tr><th>Item</th><th>Tijd</th><th>Status</th><th /></tr></thead>
                <tbody>
                  {mijn.map((i) => (
                    <tr key={i.id}>
                      <td>{i.naam}{i.werkzaamheden && <div className="klein mute">{i.werkzaamheden}</div>}</td>
                      <td><Stand item={i} /></td>
                      <td><Badge toon={TOON[i.status]}>{ITEM_STATUS_LABEL[i.status]}</Badge></td>
                      <td className="nowrap" style={{ textAlign: 'right' }}>
                        {i.status === 'GEPLAND' && knop('Start', i.id, { status: 'BEZIG' }, true)}{' '}
                        {i.status === 'BEZIG' && knop('Naar review', i.id, { status: 'REVIEW' }, true)}{' '}
                        {i.status === 'REVIEW' && knop('Terug naar bezig', i.id, { status: 'BEZIG' })}{' '}
                        {urenAan && (timer?.itemId === i.id ? <Badge toon="oranje">⏱ loopt</Badge> : <button className="knop klein merk" title="Timer starten" onClick={() => setStart(doelVan(i, m))}>▶</button>)}{' '}
                        {urenAan && <button className="knop klein" onClick={() => setTijd(doelVan(i, m))}>+ Tijd</button>}{' '}
                        {knop('Teruggeven', i.id, { eigenaarId: null })}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            ) : <div className="leeg">Je hebt nog niets opgepakt.</div>}
            <h3 style={{ marginTop: 12 }}>Vrij in deze sprint</h3>
            {vrij.length ? (
              <table className="tabel">
                <tbody>
                  {vrij.map((i) => (
                    <tr key={i.id}><td>{i.naam}</td><td>{fmtUren(i.uren)}</td><td style={{ textAlign: 'right' }}>{knop('Oppakken', i.id, { eigenaarId: eid }, true)}</td></tr>
                  ))}
                </tbody>
              </table>
            ) : <div className="leeg">Alles in de sprint is opgepakt.</div>}
            {m.backlog.length > 0 && (
              <>
                <h3 style={{ marginTop: 12 }}>Uit de backlog halen</h3>
                <p className="mute klein">Klaar met je items? Pak het volgende item van de backlog; het komt dan in deze sprint.</p>
                <table className="tabel">
                  <tbody>
                    {m.backlog.map((i) => (
                      <tr key={i.id}><td>{i.naam}</td><td>{fmtUren(i.uren)}</td><td style={{ textAlign: 'right' }}>{knop('Oppakken', i.id, { eigenaarId: eid })}</td></tr>
                    ))}
                  </tbody>
                </table>
              </>
            )}
            {overig.length > 0 && (
              <p className="klein mute" style={{ marginTop: 12 }}>
                Team: {overig.map((i) => `${i.naam} (${i.eigenaarNaam ?? '–'}, ${ITEM_STATUS_LABEL[i.status].toLowerCase()})`).join(' · ')}
              </p>
            )}
          </div>
        );
      })}
      {start && <StartTimer doel={start} onSluit={() => setStart(null)} />}
      {tijd && <TijdToevoegen doel={tijd} onSluit={() => setTijd(null)} />}
    </>
  );
}

const doelVan = (i: SprintItem, m: MijnSprint): Doel => ({ soort: 'ITEM', id: i.id, label: i.naam, sub: m.werkpakket.naam, projectId: m.project.id, projectNummer: m.project.nummer, projectNaam: m.project.naam });

/** Voor engineers: eigen taken van de afgelopen 30 dagen en de komende tijd, met terugmelden. */
export function MijnTaken() {
  const q = useQuery({ queryKey: ['mijn'], queryFn: () => api.get<{ taken: (TaakRij & { tmToken?: string })[] }>('/api/mijn-taken') });
  if (!q.data) return <Laden />;
  const vd = vandaag();
  const komend = q.data.taken.filter((t) => (t.einddatum ?? '') >= vd);
  const voorbij = q.data.taken.filter((t) => (t.einddatum ?? '') < vd).reverse();
  const Lijst = ({ taken, terug }: { taken: typeof q.data.taken; terug?: boolean }) => (
    <table className="tabel">
      <thead><tr><th>Datum</th><th>Tijd</th><th>Project</th><th>Taak</th><th>Uren</th><th>Status</th>{terug && <th />}</tr></thead>
      <tbody>
        {taken.map((t) => {
          const s = taakStatus(t, vd);
          return (
            <tr key={t.id}>
              <td className="nowrap">{fmtDag(t.startdatum)}{t.einddatum !== t.startdatum ? ` – ${fmtDag(t.einddatum)}` : ''}</td>
              <td className="nowrap">{t.van}–{t.tot}</td>
              <td>{t.projectnummer ?? t.changeNr} <span className="klein mute">{t.klant}</span></td>
              <td>{t.titel}{t.werkvorm === 'ONSITE' ? ' · 🚗 onsite' : ''}</td>
              <td>{fmtUren(t.urenPerDag)}</td>
              <td><Badge toon={s.toon}>{s.label}</Badge></td>
              {terug && <td>{t.tmToken && t.taakType !== 'OVERLEG' && t.taakType !== 'FOCUS' && <a className="knop klein" href={`/terugmelden/${t.tmToken}`}>Terugmelden</a>}</td>}
            </tr>
          );
        })}
      </tbody>
    </table>
  );
  return (
    <div className="pagina kolom" style={{ gap: 16 }}>
      <div className="paneel">
        <h1 style={{ marginBottom: 8 }}>Mijn taken</h1>
        <p className="mute klein">Tip: zet in je eigen Outlook-afspraak het projectnummer tussen blokhaken in het onderwerp, bijv. <b>[204295] Afstemming</b>. Dan komt het overleg automatisch in de planning en krijg je de urenlink.</p>
        <Lijst taken={komend} />
        {!komend.length && <div className="leeg">Geen geplande taken.</div>}
      </div>
      <MijnSprintPaneel />
      <div className="paneel">
        <h2 style={{ marginBottom: 8 }}>Afgelopen 30 dagen</h2>
        <Lijst taken={voorbij} terug />
        {!voorbij.length && <div className="leeg">Niets in de afgelopen 30 dagen.</div>}
      </div>
    </div>
  );
}
